import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
// components
import { PdvAvatars } from './PdvAvatars';
import { AgreementsMenu } from './AgremeentsMenu';
import { RsuiteTooltip } from '@commons/components';
// utils && hooks
import { COUNTRY } from 'utils/country';
import { formatDateWithLocale } from 'utils/formatDateWithLocale';
import { useUser } from 'utils/hooks';
import { STATUS_ICON, STATUS_TYPE } from '../utils';
import { disabledTooltip } from '../../utils';
// styles
import styles from './AgreementCard.module.css';

const AgreementCard = ({ agreement, userLocaleCountryCode, handleDeleteAgreement }) => {
    const { query } = useRouter();
    const {
        user: { pointsOfSale },
    } = useUser({});
    const [disabledTooltipName, setDisabledTooltipName] = useState<boolean>(false);

    const lang = userLocaleCountryCode === COUNTRY.US ? 'en' : userLocaleCountryCode === COUNTRY.BR ? 'pt-BR' : 'es';

    const {
        client,
        drug_manufacturer: { name },
        id,
        list_next_to_expire,
        lists,
        lists_quantity,
        status,
        updated_at,
        visible_percentage,
        points_of_sale,
    } = agreement;

    const getStatusIcon = () => {
        const ids = lists?.map(({ status: { id } }) => id) || [];
        if (ids.includes(STATUS_TYPE.EXPIRED)) return STATUS_ICON[STATUS_TYPE.EXPIRED];
        if (ids.includes(STATUS_TYPE.IN_PROGRESS)) return STATUS_ICON[STATUS_TYPE.IN_PROGRESS];
        return STATUS_ICON[STATUS_TYPE.DONE];
    };

    useEffect(() => {
        const checkEllipsis = () => {
            setDisabledTooltipName(disabledTooltip(`${id}-${name}`));
        };
        const timeoutId = setTimeout(checkEllipsis, 100);
        return () => clearTimeout(timeoutId);
    }, [name]);

    return (
        <Link className={styles['wrapper']} href={`/${query.clientId}/settings/trade-agreements/editAgreement/${id}`}>
            <div className={styles['description']}>
                <RsuiteTooltip
                    placement="bottom"
                    text={name}
                    trigger="hover"
                    key={id}
                    disabled={disabledTooltipName}
                    marginTop={8}
                >
                    <p id={`${id}-${name}`} className={styles['name']}>
                        {name}
                    </p>
                </RsuiteTooltip>
                <div className={styles['wrapper-quantity']}>
                    {getStatusIcon()}
                    <AgreementsMenu data={lists}>
                        <p className={styles['text-light']}>{lists_quantity}</p>
                    </AgreementsMenu>
                </div>
                <div className={styles['wrapper-updated_at']}>
                    <p className={styles['text-light']}>{formatDateWithLocale(updated_at, lang)}</p>
                </div>
                <div className={styles['wrapper-next_expire']}>
                    <p className={styles['text-light']}>
                        {list_next_to_expire?.expired_at ? formatDateWithLocale(list_next_to_expire?.expired_at, lang) : '-'}
                    </p>
                </div>
                <div className={styles['wrapper-pos']}>
                    <PdvAvatars data={points_of_sale} isAll={points_of_sale?.length === pointsOfSale?.length} />
                </div>
                <div
                    className={styles['wrapper-delete']}
                    onClick={(event: any) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleDeleteAgreement(id);
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M7 21C6.45 21 5.97917 20.8042 5.5875 20.4125C5.19583 20.0208 5 19.55 5 19V6C4.71667 6 4.47917 5.90417 4.2875 5.7125C4.09583 5.52083 4 5.28333 4 5C4 4.71667 4.09583 4.47917 4.2875 4.2875C4.47917 4.09583 4.71667 4 5 4H9C9 3.71667 9.09583 3.47917 9.2875 3.2875C9.47917 3.09583 9.71667 3 10 3H14C14.2833 3 14.5208 3.09583 14.7125 3.2875C14.9042 3.47917 15 3.71667 15 4H19C19.2833 4 19.5208 4.09583 19.7125 4.2875C19.9042 4.47917 20 4.71667 20 5C20 5.28333 19.9042 5.52083 19.7125 5.7125C19.5208 5.90417 19.2833 6 19 6V19C19 19.55 18.8042 20.0208 18.4125 20.4125C18.0208 20.8042 17.55 21 17 21H7ZM17 6H7V19H17V6ZM9 17H11V8H9V17ZM13 17H15V8H13V17Z"
                            fill="#E22E2E"
                        />
                    </svg>
                </div>
            </div>
        </Link>
    );
};

export default AgreementCard;
